// Lineup conflict detection — per plan §5 / Sprint 5 deliverables (the
// algorithm lives in Sprint 4's shared package so the Sprint 4 lineup
// builder can render conflict warnings live; Sprint 5's UI views consume
// these same results).
//
// Three conflict kinds:
//   - 'quick_change'      — same dancer in two dances < min seconds apart
//   - 'sibling_adjacent'  — siblings in adjacent or near-adjacent dances
//                           (parents can't be in two places at once)
//   - 'duplicate'         — same dancer assigned twice to the same dance
//
// All are warnings, not errors — the studio sometimes intentionally cues
// quick changes. The UI surfaces them with a "dismiss / acknowledge"
// affordance (Sprint 5 wires the override table).

export interface LineupDanceWithCast {
  dance_id: string;
  sequence_index: number;
  computed_start_time: string; // ISO
  computed_end_time: string; // ISO
  cast_dancer_ids: string[];
}

export interface DancerFamilyRef {
  dancer_id: string;
  family_id: string;
  display_name: string;
}

export type LineupConflictKind = 'quick_change' | 'sibling_adjacent' | 'duplicate';

export interface LineupConflict {
  kind: LineupConflictKind;
  severity: 'high' | 'medium' | 'low';
  message: string;
  // Dancers involved (one entry for duplicate / quick_change; two for sibling).
  dancer_ids: string[];
  // Dances involved (one for duplicate; two for quick_change / sibling).
  dance_ids: string[];
  // Gap in seconds when applicable.
  gap_seconds?: number;
}

export interface DetectLineupConflictsOptions {
  // Default thresholds — overridable per studio if a UI ever needs to.
  quickChangeMinSeconds?: number;
  siblingAdjacentWindow?: number;
}

// Exported so UI components and aggregation views use the same canonical
// thresholds — previously these were duplicated as magic numbers in
// admin.productions.$id.by-dancer.tsx and by-family.tsx.
export const CONFLICT_DEFAULTS = {
  quickChangeMinSeconds: 120, // 2 minutes — costume + getting backstage
  siblingAdjacentWindow: 2, // dances within this many positions
} as const;

const DEFAULTS = CONFLICT_DEFAULTS;

export function detectLineupConflicts(
  lineup: LineupDanceWithCast[],
  dancerFamily: ReadonlyMap<string, DancerFamilyRef>,
  options: DetectLineupConflictsOptions = {},
): LineupConflict[] {
  const opts = { ...DEFAULTS, ...options };
  const sorted = [...lineup].sort((a, b) => a.sequence_index - b.sequence_index);
  const out: LineupConflict[] = [];

  // 1. Duplicates within a single dance.
  for (const d of sorted) {
    const seen = new Map<string, number>();
    for (const id of d.cast_dancer_ids) {
      seen.set(id, (seen.get(id) ?? 0) + 1);
    }
    for (const [id, count] of seen) {
      if (count > 1) {
        out.push({
          kind: 'duplicate',
          severity: 'high',
          message: 'Same dancer listed in this dance more than once.',
          dancer_ids: [id],
          dance_ids: [d.dance_id],
        });
      }
    }
  }

  // 2. Quick changes — same dancer in dance N and dance M with gap < threshold.
  // Build dancer → dances index for efficient lookup.
  const dancerToDances = new Map<string, LineupDanceWithCast[]>();
  for (const d of sorted) {
    for (const id of new Set(d.cast_dancer_ids)) {
      const list = dancerToDances.get(id) ?? [];
      list.push(d);
      dancerToDances.set(id, list);
    }
  }
  for (const [dancerId, dances] of dancerToDances) {
    for (let i = 1; i < dances.length; i++) {
      const prev = dances[i - 1]!;
      const next = dances[i]!;
      const gap =
        (new Date(next.computed_start_time).getTime() -
          new Date(prev.computed_end_time).getTime()) /
        1000;
      if (gap < opts.quickChangeMinSeconds) {
        out.push({
          kind: 'quick_change',
          severity: gap < 30 ? 'high' : 'medium',
          message: `Quick change: ${Math.round(gap)}s between dances (target ${opts.quickChangeMinSeconds}s+).`,
          dancer_ids: [dancerId],
          dance_ids: [prev.dance_id, next.dance_id],
          gap_seconds: Math.round(gap),
        });
      }
    }
  }

  // 3. Sibling adjacency — siblings (same family) cast in dances within the
  // adjacency window. Parents can only be in one wing at a time.
  // Group dances by family across their cast.
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length && j - i <= opts.siblingAdjacentWindow; j++) {
      const danceA = sorted[i]!;
      const danceB = sorted[j]!;
      const familiesA = new Map<string, string[]>(); // family_id → [dancer_ids]
      for (const id of danceA.cast_dancer_ids) {
        const fam = dancerFamily.get(id);
        if (!fam) continue;
        const list = familiesA.get(fam.family_id) ?? [];
        list.push(id);
        familiesA.set(fam.family_id, list);
      }
      for (const dancerB of danceB.cast_dancer_ids) {
        const famB = dancerFamily.get(dancerB);
        if (!famB) continue;
        const dancersAInFamily = familiesA.get(famB.family_id);
        if (!dancersAInFamily) continue;
        for (const dancerA of dancersAInFamily) {
          if (dancerA === dancerB) continue; // same dancer is the quick-change case
          const distance = danceB.sequence_index - danceA.sequence_index;
          out.push({
            kind: 'sibling_adjacent',
            severity: distance === 1 ? 'medium' : 'low',
            message:
              distance === 1
                ? 'Siblings in back-to-back dances — parent quick-change needed backstage.'
                : `Siblings within ${distance} dances of each other.`,
            dancer_ids: [dancerA, dancerB],
            dance_ids: [danceA.dance_id, danceB.dance_id],
          });
        }
      }
    }
  }

  // Stable sort for predictable rendering: high → medium → low, then by
  // first dance's sequence_index.
  const severityRank = { high: 0, medium: 1, low: 2 };
  return out.sort((a, b) => {
    if (severityRank[a.severity] !== severityRank[b.severity]) {
      return severityRank[a.severity] - severityRank[b.severity];
    }
    const aMin = Math.min(
      ...a.dance_ids.map((id) => sorted.find((d) => d.dance_id === id)?.sequence_index ?? 999),
    );
    const bMin = Math.min(
      ...b.dance_ids.map((id) => sorted.find((d) => d.dance_id === id)?.sequence_index ?? 999),
    );
    return aMin - bMin;
  });
}
